/**
 * 1.5.0 shot detection, stream S2: the player's shot ticks and jumps (plan §6).
 *
 *  - useShotJumps: the asset's shot starts (useShotStarts, the Shots layer) and prev / next. "Previous" from inside a
 *    shot goes to its start first, then to the one before. Player.tsx calls prev on ↑ and next on ↓ (only while its
 *    keyboard is on: the editor's source viewer only while it has focus). shots null: no shots (old assets), and the
 *    keys do nothing.
 *  - ShotTicks: subtle ticks at each shot start, drawn as Timeline children, thin and low-contrast (theme tokens),
 *    hidden where they would be a few pixels apart; the "Show shots" setting (a per-user preference, on by default).
 *  - ShotJumpButtons: "Previous shot (↑)" and "Next shot (↓)" next to the frame-step buttons; nothing without shots.
 *  - ShowShotsToggle: the player's "Show shots" switch (only with shots).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { SkipBack, SkipForward } from 'lucide-react';
import type { AssetDetail } from '@prism/contracts';
import { useShotStarts } from '../api/hooks/shots';
import { useServerPref } from '../lib/prefs';

export interface ShotJumps {
  /** Shot starts in seconds, ascending; null: no shots. */
  shots: number[] | null;
  prev(): void;
  next(): void;
}

const NO_SHOTS: ShotJumps = { shots: null, prev: () => undefined, next: () => undefined };

/** The start to go to from `t` on ↑: the current shot's start when inside it (more than `eps` after it), else the one before. */
export function prevShotStart(starts: number[], t: number, eps: number): number | null {
  let lo = 0;
  let hi = starts.length - 1;
  let best = -1;
  // The latest start strictly before t - eps.
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (starts[mid]! < t - eps) { best = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return best >= 0 ? starts[best]! : starts.length ? (t > eps ? 0 : null) : null;
}

/** The start to go to from `t` on ↓: the first start more than `eps` after t; null at the last shot. */
export function nextShotStart(starts: number[], t: number, eps: number): number | null {
  let lo = 0;
  let hi = starts.length - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (starts[mid]! > t + eps) { best = mid; hi = mid - 1; } else lo = mid + 1;
  }
  return best >= 0 ? starts[best]! : null;
}

/**
 * The ticks to draw (seconds), keeping one where ticks would be closer than `minPx` on a timeline `width` px wide:
 * the first of a crowded run is kept, the next one shown is at least minPx after it. The start at 0 has no tick.
 */
export function visibleTicks(starts: number[], duration: number, width: number, minPx = 4): number[] {
  if (!duration || width <= 0) return [];
  const out: number[] = [];
  let last = -Infinity;
  for (const s of starts) {
    if (s <= 0 || s >= duration) continue;
    const x = (s / duration) * width;
    if (x - last < minPx) continue;
    out.push(s);
    last = x;
  }
  return out;
}

/** Keep the answer stable (useMemo on the shots): Player.tsx re-binds its keys when it changes. */
export function useShotJumps(asset: AssetDetail, now: () => number, seek: (seconds: number) => void): ShotJumps {
  const shots = useShotStarts(asset.id);
  const nowRef = useRef(now);
  const seekRef = useRef(seek);
  nowRef.current = now;
  seekRef.current = seek;
  const rate = asset.timing.proxyFrameRate ?? asset.timing.sourceFrameRate;
  // Half a frame and a little: a jump lands in the shot's first frame, so the next ↑ goes to the shot before.
  const eps = rate && rate.num > 0 ? (rate.den / rate.num) * 0.6 : 0.025;
  return useMemo<ShotJumps>(() => {
    if (!shots) return NO_SHOTS;
    return {
      shots,
      prev: () => { const s = prevShotStart(shots, nowRef.current(), eps); if (s != null) seekRef.current(s); },
      next: () => { const s = nextShotStart(shots, nowRef.current(), eps); if (s != null) seekRef.current(s); },
    };
  }, [shots, eps]);
}

/** The per-user "Show shots" setting (on by default). */
export function useShowShots(): [boolean, (v: boolean) => void] {
  const [show, set] = useServerPref<boolean>('player.show-shots', true);
  return [show !== false, set];
}

export function ShotTicks({ jumps, duration }: { jumps: ShotJumps; duration: number }) {
  const [show] = useShowShots();
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const active = Boolean(jumps.shots && show);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [active]);
  const ticks = useMemo(() => (jumps.shots ? visibleTicks(jumps.shots, duration, width) : []), [jumps.shots, duration, width]);
  if (!active) return null;
  return (
    <div className="shot-ticks" ref={ref} aria-hidden data-testid="shot-ticks">
      {ticks.map((s) => <span key={s} className="shot-tick" style={{ left: `${(s / duration) * 100}%` }} />)}
    </div>
  );
}

export function ShotJumpButtons({ jumps }: { jumps: ShotJumps }) {
  if (!jumps.shots) return null;
  return (
    <>
      <button className="btn ghost icon sm shot-jump" onClick={jumps.prev} aria-label="Previous shot" title="Previous shot (↑)" data-testid="prev-shot"><SkipBack size={14} /></button>
      <button className="btn ghost icon sm shot-jump" onClick={jumps.next} aria-label="Next shot" title="Next shot (↓)" data-testid="next-shot"><SkipForward size={14} /></button>
    </>
  );
}

/** "Show shots" in the player's settings row (only when the asset has shots). */
export function ShowShotsToggle({ jumps }: { jumps: ShotJumps }) {
  const [show, setShow] = useShowShots();
  // The server preference's cache notifies a tick later; the box follows the click at once.
  const [local, setLocal] = useState<boolean | null>(null);
  useEffect(() => { if (local === show) setLocal(null); }, [local, show]);
  if (!jumps.shots) return null;
  return (
    <label className="shot-toggle" title="Show the shots’ starts on the timeline (remembered for you)">
      <input type="checkbox" checked={local ?? show} onChange={(e) => { setLocal(e.target.checked); setShow(e.target.checked); }} data-testid="show-shots" /> Shots
    </label>
  );
}
